import * as React from 'react';
import { MessageCircle } from 'lucide-react';
import { AskMarkdown } from './AskMarkdown';
import { ChatComposer } from './ChatComposer';
import { PastNegotiationSourceModal } from './PastNegotiationSourceModal';
import { detectTextDirection } from '../../utils/textUtils';
import apiService from '../../services/api';
import type { PlaybookRecordPreviewData } from './PlaybookRecordClausePreview';
import {
  buildPlaybookRecordIndex,
  getPlaybookRecord,
  type PlaybookArchiveRow,
  type PlaybookRecordIndex,
} from '../../utils/playbookRecordIndex';
import { handleError } from '../../utils/errorHandling';
import {
  ASK_AGENT_CONTEXT_SOURCE_CHOICE_ARCHIVE,
  ASK_AGENT_CONTEXT_SOURCE_CHOICE_BANK,
  ASK_AGENT_CONTEXT_SOURCE_CHOICE_BOTH,
  ASK_AGENT_CONTEXT_SOURCE_UNRECOGNIZED,
  ASK_AGENT_DEEP_RESEARCH_DECLINED_REPLY,
  ASK_AGENT_DEEP_RESEARCH_QUESTION,
  ASK_AGENT_SUGGEST_EDIT_APPLIED_REPLY,
  ASK_AGENT_SUGGEST_EDIT_DECLINED_REPLY,
  ASK_AGENT_SUGGEST_EDIT_OFFER,
  getAskAgentContextSourceAppliedReply,
  isDeepResearchAffirmative,
  isDeepResearchDecline,
  isSuggestEditRecommendation,
  parseAskAgentContextSourceChoice,
  type AskAgentContextSource,
} from '../../utils/clauseAskAgentContext';
import './PlaybookAgentBesidePanel.css';

export type PlaybookAgentChatTurn = { id: string; role: 'user' | 'assistant'; content: string };

export interface PlaybookAgentBesidePanelProps {
  projectLabel: string;
  recordCount: number;
  contextBody: string;
  clauseNumberDisplay?: string | null;
  /** Sent automatically once when matched past negotiations exist. */
  initialQuestion?: string | null;
  /** Shown immediately when there are no matched past negotiations (no API call). */
  openingAssistantMessage?: string | null;
  /** User may opt in to deep playbook search after openingAssistantMessage. */
  awaitingDeepResearchConsent?: boolean;
  /** User must pick BoC vs project archive vs both before the first question. */
  awaitingContextSourceChoice?: boolean;
  contextSource?: AskAgentContextSource | null;
  onChooseContextSource?: (source: AskAgentContextSource) => void;
  /** Apply AI track-changes suggestion to the clause client request from chat context. */
  onApplySuggestedEdit?: (messages: PlaybookAgentChatTurn[]) => Promise<void>;
  isApplyingSuggestedEdit?: boolean;
  /** Archive rows in display order (Record 1 … N); enables clickable Record references in answers. */
  playbookRows?: PlaybookArchiveRow[];
  projectId?: string | null;
  playbookDomain?: 'construction-loan' | 'real-estate';
}

export const PlaybookAgentBesidePanel: React.FC<PlaybookAgentBesidePanelProps> = ({
  projectLabel,
  recordCount,
  contextBody,
  clauseNumberDisplay,
  initialQuestion,
  openingAssistantMessage,
  awaitingDeepResearchConsent = false,
  awaitingContextSourceChoice = false,
  contextSource = null,
  onChooseContextSource,
  onApplySuggestedEdit,
  isApplyingSuggestedEdit = false,
  playbookRows = [],
  projectId = null,
  playbookDomain = 'construction-loan',
}) => {
  const [messages, setMessages] = React.useState<PlaybookAgentChatTurn[]>([]);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [deepResearchConsentPending, setDeepResearchConsentPending] = React.useState(
    awaitingDeepResearchConsent,
  );
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const initialQuestionSentRef = React.useRef(false);
  const openingMessageShownRef = React.useRef(false);
  const contextBodyRef = React.useRef(contextBody);
  const contextSourceRef = React.useRef(contextSource);
  React.useEffect(() => {
    contextBodyRef.current = contextBody;
  }, [contextBody]);
  React.useEffect(() => {
    if (contextSource && contextSource !== contextSourceRef.current) {
      initialQuestionSentRef.current = false;
    }
    contextSourceRef.current = contextSource;
  }, [contextSource]);
  const [suggestEditOfferDismissed, setSuggestEditOfferDismissed] = React.useState(false);
  const [suggestEditApplied, setSuggestEditApplied] = React.useState(false);
  const [previewRecordNum, setPreviewRecordNum] = React.useState<number | null>(null);

  const recordIndex: PlaybookRecordIndex = React.useMemo(
    () => buildPlaybookRecordIndex(playbookRows),
    [playbookRows],
  );

  const validRecordNums = React.useMemo(
    () => new Set(recordIndex.byNumber.keys()),
    [recordIndex],
  );

  const previewCacheRef = React.useRef<Map<string, PlaybookRecordPreviewData>>(new Map());

  const loadRecordPreview = React.useCallback(
    async (row: PlaybookArchiveRow): Promise<PlaybookRecordPreviewData> => {
      const cached = previewCacheRef.current.get(row.id);
      if (cached && !cached.loading) return cached;

      const fallback: PlaybookRecordPreviewData = {
        plainText: row.paragraph_text?.trim() || row.paragraph_preview || null,
        side: row.side === 'our_side' || row.side === 'their_side' ? row.side : null,
        loading: true,
      };
      previewCacheRef.current.set(row.id, fallback);

      if (!projectId) return { ...fallback, loading: false };

      try {
        const res = await apiService.getPastContractsPlaybooksByIds(
          projectId,
          [row.id],
          playbookDomain,
        );
        const hit = res.data?.find((r) => r.id === row.id);
        const side =
          hit?.side === 'our_side' || hit?.side === 'their_side' ? hit.side : fallback.side;
        const data: PlaybookRecordPreviewData = {
          xml: hit?.paragraph_xml?.trim() || null,
          plainText:
            hit?.paragraph_text?.trim() ||
            row.paragraph_text?.trim() ||
            row.paragraph_preview ||
            null,
          side: side ?? null,
          loading: false,
        };
        previewCacheRef.current.set(row.id, data);
        return data;
      } catch {
        const errData: PlaybookRecordPreviewData = {
          ...fallback,
          loading: false,
          error: true,
        };
        previewCacheRef.current.set(row.id, errData);
        return errData;
      }
    },
    [projectId, playbookDomain],
  );

  const recordLinkOpts = React.useMemo(
    () =>
      contextSource !== 'bank_of_changes' &&
      projectId &&
      validRecordNums.size > 0
        ? {
            validRecordNums,
            onRecordClick: (n: number) => setPreviewRecordNum(n),
            getRecord: (n: number) => getPlaybookRecord(recordIndex, n),
            loadPreview: loadRecordPreview,
          }
        : undefined,
    [contextSource, projectId, validRecordNums, recordIndex, loadRecordPreview],
  );

  const previewRow =
    previewRecordNum != null ? getPlaybookRecord(recordIndex, previewRecordNum) : undefined;

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = React.useCallback(
    async (apiText: string, userDisplay?: string): Promise<boolean> => {
      const trimmedApi = apiText.trim();
      if (!trimmedApi || sending) return false;
      const display = (userDisplay ?? apiText).trim() || trimmedApi;

      const userMsg: PlaybookAgentChatTurn = { id: `u-${Date.now()}`, role: 'user', content: display };
      setMessages((prev) => [...prev, userMsg]);
      setSending(true);

      const prior = messages.map((m) => ({ role: m.role, content: m.content }));

      try {
        const res = await apiService.playbookAsk(
          contextBodyRef.current,
          trimmedApi,
          prior,
          projectLabel,
        );
        if (!res.success || !res.data?.answer) {
          throw new Error(res.message || res.error || 'No answer returned');
        }
        setMessages((prev) => [
          ...prev,
          { id: `a-${Date.now()}`, role: 'assistant', content: res.data!.answer },
        ]);
        return true;
      } catch (e) {
        const msg = handleError(e);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: `Sorry — something went wrong: ${msg}`,
          },
        ]);
        return true;
      } finally {
        setSending(false);
      }
    },
    [contextBody, messages, projectLabel, sending],
  );

  React.useEffect(() => {
    const notice = openingAssistantMessage?.trim();
    if (!notice || openingMessageShownRef.current || !contextBody.trim()) return;
    openingMessageShownRef.current = true;
    setMessages([{ id: `a-open-${Date.now()}`, role: 'assistant', content: notice }]);
  }, [openingAssistantMessage, contextBody]);

  React.useEffect(() => {
    const q = initialQuestion?.trim();
    if (
      !q ||
      !contextBody.trim() ||
      initialQuestionSentRef.current ||
      awaitingContextSourceChoice
    ) {
      return;
    }
    if (contextSource === 'bank_of_changes' && !contextBody.includes('Bank of Changes option')) {
      return;
    }

    const sendInitial = () => {
      if (initialQuestionSentRef.current) return;
      const body = contextBodyRef.current;
      if (!body.trim()) return;
      if (contextSource === 'bank_of_changes' && !body.includes('Bank of Changes option')) {
        return;
      }
      initialQuestionSentRef.current = true;
      void handleSend(q);
    };

    requestAnimationFrame(() => requestAnimationFrame(sendInitial));
  }, [
    initialQuestion,
    contextBody,
    contextSource,
    handleSend,
    awaitingContextSourceChoice,
  ]);

  const applyContextSourceChoice = React.useCallback(
    (source: AskAgentContextSource, userLabel: string) => {
      if (!onChooseContextSource) return;
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', content: userLabel },
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: getAskAgentContextSourceAppliedReply(source),
        },
      ]);
      onChooseContextSource(source);
    },
    [onChooseContextSource],
  );

  const submitUserMessage = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (awaitingContextSourceChoice && onChooseContextSource) {
        const parsed = parseAskAgentContextSourceChoice(trimmed);
        if (parsed) {
          applyContextSourceChoice(parsed, trimmed);
          return;
        }
        setMessages((prev) => [
          ...prev,
          { id: `u-${Date.now()}`, role: 'user', content: trimmed },
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: ASK_AGENT_CONTEXT_SOURCE_UNRECOGNIZED,
          },
        ]);
        return;
      }

      if (deepResearchConsentPending) {
        if (isDeepResearchDecline(trimmed)) {
          setDeepResearchConsentPending(false);
          setMessages((prev) => [
            ...prev,
            { id: `u-${Date.now()}`, role: 'user', content: trimmed },
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: ASK_AGENT_DEEP_RESEARCH_DECLINED_REPLY,
            },
          ]);
          return;
        }
        if (isDeepResearchAffirmative(trimmed)) {
          setDeepResearchConsentPending(false);
          const userLabel =
            trimmed.length <= 30 ? trimmed : 'כן, חפש בארכיון';
          await handleSend(ASK_AGENT_DEEP_RESEARCH_QUESTION, userLabel);
          return;
        }
      }

      await handleSend(trimmed);
    },
    [awaitingContextSourceChoice, onChooseContextSource, applyContextSourceChoice, deepResearchConsentPending, handleSend],
  );

  const lastAssistantMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') return messages[i];
    }
    return null;
  }, [messages]);

  const showSuggestEditOffer =
    Boolean(onApplySuggestedEdit) &&
    Boolean(lastAssistantMessage) &&
    isSuggestEditRecommendation(lastAssistantMessage!.content) &&
    !suggestEditOfferDismissed &&
    !suggestEditApplied &&
    !deepResearchConsentPending &&
    !awaitingContextSourceChoice &&
    !sending &&
    !isApplyingSuggestedEdit;

  const handleApplySuggestedEdit = React.useCallback(async () => {
    if (!onApplySuggestedEdit || isApplyingSuggestedEdit) return;
    const userMsg: PlaybookAgentChatTurn = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: 'כן, הצע עריכה',
    };
    let snapshot: PlaybookAgentChatTurn[] = [];
    setMessages((prev) => {
      snapshot = [...prev, userMsg];
      return snapshot;
    });
    try {
      await onApplySuggestedEdit(snapshot);
      setSuggestEditApplied(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: ASK_AGENT_SUGGEST_EDIT_APPLIED_REPLY,
        },
      ]);
    } catch (e) {
      const msg = handleError(e);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: `לא הצלחתי להחיל את העריכה: ${msg}`,
        },
      ]);
    }
  }, [onApplySuggestedEdit, isApplyingSuggestedEdit]);

  const handleDeclineSuggestedEdit = React.useCallback(() => {
    setSuggestEditOfferDismissed(true);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: 'לא, תודה' },
      {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: ASK_AGENT_SUGGEST_EDIT_DECLINED_REPLY,
      },
    ]);
  }, []);

  const inputDisabled = !contextBody.trim() || sending || isApplyingSuggestedEdit;
  const showEmpty = messages.length === 0 && !sending && !isApplyingSuggestedEdit;

  return (
    <div className="pba-panel">
      <div className="pba-messages">
        {showEmpty && (
          <div className="pba-empty">
            <MessageCircle className="pba-empty-icon" size={36} aria-hidden />
            <p className="pba-empty-title">Ask about past negotiations</p>
            <p className="pba-empty-hint">
              Context includes the client request, interpretation, matched past negotiations, and
              the playbook for <strong>{projectLabel}</strong> ({recordCount} archive record
              {recordCount === 1 ? '' : 's'}).
              {clauseNumberDisplay ? ` Opened from clause ${clauseNumberDisplay}.` : ''}
            </p>
          </div>
        )}

        {messages.map((m) => {
          const dir = detectTextDirection(m.content);
          const isUser = m.role === 'user';
          return (
            <div key={m.id} className={`pba-bubble pba-bubble-${m.role}`}>
              <div className="pba-bubble-role">{isUser ? 'You' : 'Courtyard'}</div>
              {isUser ? (
                <div
                  className="pba-bubble-content"
                  dir={dir}
                  style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }}
                >
                  {m.content}
                </div>
              ) : (
                <AskMarkdown content={m.content} dir={dir} recordLinks={recordLinkOpts} />
              )}
            </div>
          );
        })}

        {showSuggestEditOffer && (
          <div className="pba-suggest-edit-offer">
            <p className="pba-suggest-edit-offer-text" dir="rtl">
              {ASK_AGENT_SUGGEST_EDIT_OFFER}
            </p>
            <div className="pba-quick-replies" role="group" aria-label="עריכה מוצעת">
              <button
                type="button"
                className="pba-quick-reply pba-quick-reply-primary"
                disabled={isApplyingSuggestedEdit}
                onClick={() => void handleApplySuggestedEdit()}
              >
                כן, הצע עריכה
              </button>
              <button
                type="button"
                className="pba-quick-reply"
                disabled={isApplyingSuggestedEdit}
                onClick={handleDeclineSuggestedEdit}
              >
                לא, תודה
              </button>
            </div>
          </div>
        )}

        {awaitingContextSourceChoice && onChooseContextSource && !sending && !showSuggestEditOffer && (
          <div className="pba-quick-replies" role="group" aria-label="בחירת מקור להקשר">
            <button
              type="button"
              className="pba-quick-reply pba-quick-reply-primary"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice('bank_of_changes', ASK_AGENT_CONTEXT_SOURCE_CHOICE_BANK)
              }
            >
              {ASK_AGENT_CONTEXT_SOURCE_CHOICE_BANK}
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice(
                  'past_negotiations',
                  ASK_AGENT_CONTEXT_SOURCE_CHOICE_ARCHIVE,
                )
              }
            >
              {ASK_AGENT_CONTEXT_SOURCE_CHOICE_ARCHIVE}
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice('both', ASK_AGENT_CONTEXT_SOURCE_CHOICE_BOTH)
              }
            >
              {ASK_AGENT_CONTEXT_SOURCE_CHOICE_BOTH}
            </button>
          </div>
        )}

        {deepResearchConsentPending && !sending && !showSuggestEditOffer && !awaitingContextSourceChoice && (
          <div className="pba-quick-replies" role="group" aria-label="תשובות מהירות">
            <button
              type="button"
              className="pba-quick-reply pba-quick-reply-primary"
              disabled={sending}
              onClick={() => {
                setDeepResearchConsentPending(false);
                void handleSend(ASK_AGENT_DEEP_RESEARCH_QUESTION, 'כן, חפש בארכיון');
              }}
            >
              כן, חפש בארכיון
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() => void submitUserMessage('לא')}
            >
              לא, תודה
            </button>
          </div>
        )}

        {(sending || isApplyingSuggestedEdit) && (
          <div className="pba-bubble pba-bubble-assistant">
            <div className="pba-bubble-role">Courtyard</div>
            <div className="pba-typing" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="pba-input-area">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={async ({ text }) => {
            const t = text.trim();
            if (!t || sending) return;
            await submitUserMessage(t);
            setDraft('');
          }}
          disabled={inputDisabled}
          submitting={sending}
          placeholder={
            awaitingContextSourceChoice
              ? 'בחר/י מקור או הקלד: בנק השינויים / ארכיון / שניהם…'
              : deepResearchConsentPending
                ? 'הקלד כן או לא, או שאל שאלה…'
                : 'שאל על משא ומתן קודם…'
          }
          layout="sticky"
          followUp={messages.length > 0}
          submitTitle="Send question"
          showAgentModeSelector={false}
        />
      </div>

      {previewRecordNum != null && projectId && previewRow && (
        <PastNegotiationSourceModal
          filename={previewRow.filename?.trim() || 'Source unavailable'}
          filenameDir={detectTextDirection(previewRow.filename || '')}
          projectId={projectId}
          playbookId={previewRow.id}
          searchText={previewRow.paragraph_text?.trim() || previewRow.paragraph_preview}
          domain={playbookDomain}
          onClose={() => setPreviewRecordNum(null)}
        />
      )}
    </div>
  );
};